import { useEffect, useState } from 'react';
import type { FileItem, ShareLink } from '../lib/types';
import { Modal, Button, Spinner, ErrorText } from './ui';
import { createShareLink, revokeShareLink, ApiError } from '../lib/api';
import { listSharesForFile } from '../lib/data';

export function ShareDialog({ file, onClose }: { file: FileItem; onClose: () => void }) {
  const [links, setLinks] = useState<ShareLink[] | null>(null);
  const [newUrl, setNewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  async function load() {
    setLinks(await listSharesForFile(file.id));
  }
  useEffect(() => {
    load().catch(() => setLinks([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.id]);

  async function create() {
    setBusy(true);
    setError('');
    try {
      const r = await createShareLink(file.id);
      setNewUrl(r.url);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to create link');
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    await revokeShareLink(file.id, id);
    await load();
  }

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may be unavailable */
    }
  }

  return (
    <Modal title={`Share “${file.name}”`} onClose={onClose}>
      <p className="text-sm text-slate-600">
        Creates a <strong>one-time</strong> link: it works for a single download, then stops working.
      </p>
      <div className="mt-3">
        <Button onClick={create} disabled={busy}>
          {busy ? 'Creating…' : 'Create one-time link'}
        </Button>
      </div>
      <ErrorText>{error}</ErrorText>

      {newUrl && (
        <div className="mt-3 rounded bg-slate-50 p-2">
          <p className="text-xs font-medium text-slate-500">New link</p>
          <div className="mt-1 flex items-center gap-2">
            <input
              readOnly
              value={newUrl}
              className="w-full rounded border border-slate-200 bg-white px-2 py-1 text-sm"
            />
            <Button variant="secondary" onClick={() => copy(newUrl)}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
      )}

      <div className="mt-4">
        <p className="text-xs font-medium text-slate-500">Links</p>
        {links === null ? (
          <Spinner />
        ) : links.length === 0 ? (
          <p className="text-sm text-slate-400">No links yet.</p>
        ) : (
          <ul className="mt-1 space-y-1 text-sm">
            {links.map((l) => (
              <li key={l.id} className="flex items-center justify-between gap-2">
                <span className="truncate font-mono text-xs">
                  /s/{l.token.slice(0, 10)}…{' '}
                  <span className="rounded bg-slate-100 px-1 font-sans text-slate-500">{l.status}</span>
                </span>
                {l.status === 'active' && (
                  <Button variant="ghost" onClick={() => revoke(l.id)}>
                    Revoke
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
